<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500 hover:bg-gray-50"
                                        title="Back to Categories"
                                        aria-label="Back to Categories"
                                        @click="$router.push('/themecategory')"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div class="flex-1 min-w-0">
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0 truncate">{{ category.name || (isLoading ? 'Loading...' : 'Untitled Category') }}</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Category ID: {{ categoryId }}</p>
                                </div>
                                <div v-if="!isLoading" class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                :disabled="isSaving"
                                                @click="saveCategory"
                                        >
                                                <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                        </button>
                                </div>
                        </div>
                </header>

                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading category...</span>
                </div>

                <main v-else class="p-6">
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Tab Headers -->
                                <div class="flex border-b border-gray-200 overflow-x-auto">
                                        <button
                                                v-for="tab in tabs"
                                                :key="tab.id"
                                                type="button"
                                                class="py-4 px-6 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                                                :class="{ 'text-orange-7 border-orange-7 bg-orange-1': activeTab === tab.id, 'text-gray-500 border-transparent hover:text-gray-700': activeTab !== tab.id }"
                                                @click="activeTab = tab.id"
                                        >
                                                {{ tab.label }}
                                                <span
                                                        v-if="tab.id === 'themes'"
                                                        class="ml-2 text-xs bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full"
                                                        :class="{ 'bg-orange-2 text-orange-7': activeTab === 'themes' }"
                                                >
                                                        {{ linkedThemes.length }}
                                                </span>
                                        </button>
                                </div>

                                <!-- Tab Content -->
                                <div class="p-6">
                                        <!-- Properties Tab -->
                                        <div v-if="activeTab === 'properties'">
                                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div class="mb-0">
                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                                <input
                                                                        v-model="category.name"
                                                                        type="text"
                                                                        placeholder="Enter category name"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
                                                                >
                                                        </div>

                                                        <div class="mb-0">
                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Locale</label>
                                                                <input
                                                                        v-model="category.locale"
                                                                        type="text"
                                                                        placeholder="e.g., en, de, fr"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
                                                                >
                                                        </div>

                                                        <div class="mb-0">
                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                                                                <input
                                                                        v-model.number="category.serialnumber"
                                                                        type="number"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
                                                                >
                                                        </div>

                                                        <div class="mb-0">
                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Tracker</label>
                                                                <select
                                                                        v-model="category.trackerid"
                                                                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border bg-white focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
                                                                >
                                                                        <option :value="undefined">— Select Tracker —</option>
                                                                        <option
                                                                                v-for="t in trackers"
                                                                                :key="t.id"
                                                                                :value="t.id"
                                                                        >
                                                                                {{ t.name }}
                                                                        </option>
                                                                </select>
                                                        </div>

                                                        <div class="md:col-span-2 mb-0">
                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Category Icon</label>
                                                                <div class="flex items-start gap-4">
                                                                        <img
                                                                                v-if="category.icon"
                                                                                :src="category.icon"
                                                                                alt="Category icon"
                                                                                class="w-24 h-24 rounded object-contain border border-gray-200 bg-gray-50"
                                                                        >
                                                                        <div v-else class="w-24 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-sm border border-gray-200">No icon</div>
                                                                        <div class="flex flex-col gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-md px-4 py-2 text-sm cursor-pointer transition-colors"
                                                                                        @click="uploadIcon"
                                                                                >
                                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                                                        </svg>
                                                                                        {{ category.icon ? 'Change Icon' : 'Upload Icon' }}
                                                                                </button>
                                                                                <button
                                                                                        v-if="category.icon"
                                                                                        type="button"
                                                                                        class="bg-red-50 hover:bg-red-100 text-red-600 border-none rounded px-3 py-1.5 text-sm cursor-pointer transition-colors"
                                                                                        @click="removeIcon"
                                                                                >
                                                                                        Remove
                                                                                </button>
                                                                                <span v-if="iconFileName" class="text-xs text-gray-500 break-all max-w-[200px]">{{ iconFileName }}</span>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Linked Themes Tab -->
                                        <div v-if="activeTab === 'themes'">
                                                <div class="flex items-center justify-between mb-4">
                                                        <div>
                                                                <div class="flex items-center gap-2">
                                                                        <h2 class="text-base font-semibold text-gray-900 m-0">Linked Themes</h2>
                                                                        <span v-if="isReorderingThemes" class="inline-flex items-center gap-1 text-xs text-gray-500" role="status" aria-live="polite">
                                                                                <svg class="w-3 h-3 animate-spin text-orange-5" fill="none" viewBox="0 0 24 24">
                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                                                                </svg>
                                                                                Saving order…
                                                                        </span>
                                                                </div>
                                                                <p class="text-xs text-gray-500 mt-1 mb-0">Themes that belong to this category</p>
                                                        </div>
                                                        <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">{{ linkedThemes.length }} themes</span>
                                                </div>

                                                <!-- Theme search / add picker -->
                                                <div class="mb-4 relative" ref="themePickerRef">
                                                        <div class="relative">
                                                                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                </svg>
                                                                <input
                                                                        v-model="themeSearch"
                                                                        type="text"
                                                                        :placeholder="canSearchThemes ? 'Search themes by name or ID to add...' : 'Category has no group/type — cannot search themes'"
                                                                        :disabled="!canSearchThemes"
                                                                        class="w-full py-2 pl-9 pr-9 border border-gray-300 rounded-lg text-sm bg-white box-border outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
                                                                        @focus="themePickerOpen = true"
                                                                        @input="themePickerOpen = true"
                                                                        @keydown.esc="themePickerOpen = false"
                                                                >
                                                                <svg v-if="isLoadingAvailableThemes || isLinking" class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                                </svg>
                                                                <button
                                                                        v-else-if="themeSearch"
                                                                        type="button"
                                                                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-1"
                                                                        title="Clear"
                                                                        aria-label="Clear search"
                                                                        @click="themeSearch = ''"
                                                                >
                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                        </svg>
                                                                </button>
                                                        </div>
                                                        <div
                                                                v-if="themePickerOpen && canSearchThemes"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-[320px] overflow-y-auto"
                                                        >
                                                                <div v-if="isLoadingAvailableThemes" class="py-6 text-center text-sm text-gray-500">
                                                                        Loading themes...
                                                                </div>
                                                                <div v-else-if="filteredAvailableThemes.length === 0" class="py-6 text-center text-sm text-gray-400">
                                                                        {{ themeSearch.trim() ? 'No themes match your search' : 'No themes available for this category\'s group/type' }}
                                                                </div>
                                                                <div
                                                                        v-for="theme in filteredAvailableThemes"
                                                                        v-else
                                                                        :key="theme.id"
                                                                        :role="linkedThemeIds.has(theme.id) ? undefined : 'button'"
                                                                        :tabindex="linkedThemeIds.has(theme.id) ? -1 : 0"
                                                                        :aria-disabled="linkedThemeIds.has(theme.id) || isLinking"
                                                                        :class="[
                                                                                'flex items-center gap-3 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0',
                                                                                linkedThemeIds.has(theme.id)
                                                                                        ? 'opacity-60 cursor-not-allowed bg-gray-50'
                                                                                        : 'cursor-pointer hover:bg-orange-1 focus:outline-none focus:ring-2 focus:ring-orange-5 focus:ring-inset',
                                                                        ]"
                                                                        @click="linkedThemeIds.has(theme.id) ? null : linkTheme(theme)"
                                                                        @keydown.enter.prevent="linkedThemeIds.has(theme.id) ? null : linkTheme(theme)"
                                                                        @keydown.space.prevent="linkedThemeIds.has(theme.id) ? null : linkTheme(theme)"
                                                                >
                                                                        <img
                                                                                v-if="theme.icon"
                                                                                :src="theme.icon ?? undefined"
                                                                                class="w-9 h-9 rounded object-cover border border-gray-200 shrink-0"
                                                                                alt=""
                                                                        >
                                                                        <div v-else class="w-9 h-9 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-[9px] text-gray-400 shrink-0">No img</div>
                                                                        <div class="flex-1 min-w-0">
                                                                                <div class="text-sm font-medium text-gray-900 truncate">{{ theme.name || 'Untitled Theme' }}</div>
                                                                                <div class="text-xs text-gray-500 font-mono">ID: {{ theme.id }}</div>
                                                                        </div>
                                                                        <span
                                                                                v-if="linkedThemeIds.has(theme.id)"
                                                                                class="text-xs bg-gray-200 text-gray-600 py-0.5 px-2 rounded-full font-medium shrink-0"
                                                                        >
                                                                                Already linked
                                                                        </span>
                                                                        <svg
                                                                                v-else
                                                                                class="w-4 h-4 text-orange-7 shrink-0"
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                stroke="currentColor"
                                                                                stroke-width="2"
                                                                        >
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                        </svg>
                                                                </div>
                                                        </div>
                                                </div>

                                                <div v-if="isLoadingThemes" class="flex justify-center items-center py-12 px-4">
                                                        <svg class="w-6 h-6 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <span class="ml-3 text-gray-500 text-sm">Loading themes...</span>
                                                </div>

                                                <div v-else-if="linkedThemes.length === 0" class="text-center py-12 px-4">
                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                        </svg>
                                                        <p class="text-gray-500 text-sm m-0">No themes linked to this category</p>
                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Themes can be linked from the Themes module</p>
                                                </div>

                                                <draggable
                                                        v-else
                                                        v-model="linkedThemes"
                                                        item-key="id"
                                                        handle=".theme-drag-handle"
                                                        ghost-class="opacity-50"
                                                        :disabled="isReorderingThemes"
                                                        class="border border-gray-200 rounded-lg overflow-hidden"
                                                        @end="onReorderLinkedThemes"
                                                >
                                                        <template #item="{ element: theme, index }">
                                                                <div
                                                                        role="button"
                                                                        tabindex="0"
                                                                        :aria-label="`Open theme ${theme.name || theme.id}`"
                                                                        class="flex items-center gap-4 px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-5 focus:ring-inset"
                                                                        @click="viewTheme(theme.id)"
                                                                        @keydown.enter.prevent="viewTheme(theme.id)"
                                                                        @keydown.space.prevent="viewTheme(theme.id)"
                                                                >
                                                                        <div
                                                                                class="theme-drag-handle p-1 text-gray-400 hover:text-gray-600 shrink-0 cursor-grab active:cursor-grabbing"
                                                                                :class="{ 'opacity-50 cursor-not-allowed': isReorderingThemes }"
                                                                                title="Drag to reorder"
                                                                                aria-label="Drag to reorder"
                                                                                @click.stop
                                                                                @keydown.stop
                                                                        >
                                                                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                                        <path d="M7 4a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm6-10a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2z" />
                                                                                </svg>
                                                                        </div>
                                                                        <div class="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-500 shrink-0">{{ index + 1 }}</div>
                                                                        <img
                                                                                v-if="theme.icon"
                                                                                :src="theme.icon ?? undefined"
                                                                                class="w-12 h-12 rounded object-cover border border-gray-200 shrink-0"
                                                                                alt="Theme"
                                                                        >
                                                                        <div v-else class="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-[10px] text-gray-400 border border-gray-200 shrink-0">No image</div>
                                                                        <div class="flex-1 min-w-0">
                                                                                <div class="text-sm font-medium text-gray-900 truncate">{{ theme.name || 'Untitled Theme' }}</div>
                                                                                <div class="text-xs text-gray-500 font-mono">ID: {{ theme.id }}</div>
                                                                        </div>
                                                                        <button
                                                                                type="button"
                                                                                class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                title="Open theme"
                                                                                aria-label="Open theme"
                                                                                @click.stop="viewTheme(theme.id)"
                                                                        >
                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                </svg>
                                                                        </button>
                                                                        <button
                                                                                type="button"
                                                                                class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                title="Unlink theme"
                                                                                aria-label="Unlink theme"
                                                                                :disabled="unlinkingThemeId === theme.id"
                                                                                @click.stop="unlinkTheme(theme)"
                                                                                @keydown.enter.stop
                                                                                @keydown.space.stop
                                                                        >
                                                                                <svg v-if="unlinkingThemeId === theme.id" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                                                </svg>
                                                                                <svg v-else class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                                                                                </svg>
                                                                        </button>
                                                                </div>
                                                        </template>
                                                </draggable>
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ThemeCategoryModel, ThemeModel, AffiliateModel } from '@sosocio/creator/interfaces';
import { PickerResponse } from 'filestack-js';
import Swal from 'sweetalert2';
import draggable from 'vuedraggable';
import ThemeCategoryService from 'services/ThemeCategoryService';
import ThemeCategoryLinkService from 'services/ThemeCategoryLinkService';
import ThemeService from 'services/ThemeService';
import AffiliateService from 'services/AffiliateService';

export default defineComponent({
        name: 'EditThemeCategoryPage',
        components: { draggable },
        setup() {
                const route = useRoute();
                const router = useRouter();
                const instance = getCurrentInstance();
                const filestackClient = instance?.appContext.config.globalProperties.$client;

                const categoryId = route.params.id as string;

                const category = ref<Partial<ThemeCategoryModel>>({});
                const linkedThemes = ref<ThemeModel[]>([]);
                const trackers = ref<AffiliateModel[]>([]);
                const isLoading = ref(true);
                const isSaving = ref(false);
                const isLoadingThemes = ref(false);
                const activeTab = ref<'properties' | 'themes'>('properties');
                const iconFileName = ref('');

                // Theme search/add state
                const availableThemes = ref<ThemeModel[]>([]);
                const isLoadingAvailableThemes = ref(false);
                const themeSearch = ref('');
                const themePickerOpen = ref(false);
                const themePickerRef = ref<HTMLElement | null>(null);
                const isLinking = ref(false);
                const unlinkingThemeId = ref<number | null>(null);
                const isReorderingThemes = ref(false);
                let lastSavedThemeOrder: number[] = [];

                const linkedThemeIds = computed(() => new Set(linkedThemes.value.map(t => t.id)));

                const canSearchThemes = computed(() => {
                        const g = category.value.groupid;
                        const t = category.value.typeid;
                        return g != null && t != null;
                });

                const filteredAvailableThemes = computed(() => {
                        const q = debouncedThemeSearch.value.trim().toLowerCase();
                        let list = availableThemes.value;
                        if (q) {
                                list = list.filter(t =>
                                        String(t.name ?? '').toLowerCase().includes(q) ||
                                        String(t.id ?? '').includes(q),
                                );
                        }
                        // Sort: not-yet-linked first, then by name
                        return list
                                .slice()
                                .sort((a, b) => {
                                        const aLinked = linkedThemeIds.value.has(a.id) ? 1 : 0;
                                        const bLinked = linkedThemeIds.value.has(b.id) ? 1 : 0;
                                        if (aLinked !== bLinked) return aLinked - bLinked;
                                        return String(a.name ?? '').localeCompare(String(b.name ?? ''));
                                })
                                .slice(0, 50);
                });

                const tabs = computed(() => [
                        { id: 'properties' as const, label: 'Properties' },
                        { id: 'themes' as const, label: 'Linked Themes' },
                ]);

                const loadCategory = async () => {
                        isLoading.value = true;
                        try {
                                const data = await ThemeCategoryService.get(categoryId);
                                category.value = data;
                        } catch (error: any) {
                                console.error('Failed to load category:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load category',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadLinkedThemes = async () => {
                        isLoadingThemes.value = true;
                        try {
                                const themes = await ThemeCategoryService.getAllThemes(categoryId);
                                // The themes endpoint returns plain theme records without the join's
                                // serialnumber, so fetch the link records (sorted by serialnumber) and
                                // use them to order the themes. If the link fetch fails, fall back to
                                // the themes-endpoint order rather than failing the whole load.
                                let orderByThemeId = new Map<number, number>();
                                try {
                                        const links = await ThemeCategoryLinkService.getAll(new URLSearchParams({
                                                limit: '0',
                                                where: JSON.stringify({ categoryid: Number(categoryId) }),
                                                orderby: 'serialnumber ASC',
                                        }));
                                        orderByThemeId = new Map(links.map((link, index) => [link.themeid, index]));
                                } catch (linkError: any) {
                                        console.error('Failed to load theme order, showing default order:', linkError);
                                }
                                // Any theme without a matching link is appended last, preserving the
                                // themes-endpoint order among themselves.
                                const orderedThemes = themes
                                        .map((theme, index) => ({ theme, index }))
                                        .sort((a, b) => {
                                                const aOrder = orderByThemeId.get(a.theme.id);
                                                const bOrder = orderByThemeId.get(b.theme.id);
                                                if (aOrder == null && bOrder == null) return a.index - b.index;
                                                if (aOrder == null) return 1;
                                                if (bOrder == null) return -1;
                                                return aOrder - bOrder;
                                        })
                                        .map(({ theme }) => theme);
                                linkedThemes.value = orderedThemes;
                                lastSavedThemeOrder = orderedThemes.map(t => t.id);
                        } catch (error: any) {
                                console.error('Failed to load linked themes:', error);
                        } finally {
                                isLoadingThemes.value = false;
                        }
                };

                const onReorderLinkedThemes = async () => {
                        if (isReorderingThemes.value) return;
                        const newOrder = linkedThemes.value.map(t => t.id);
                        const orderChanged =
                                newOrder.length !== lastSavedThemeOrder.length ||
                                newOrder.some((id, i) => id !== lastSavedThemeOrder[i]);
                        if (!orderChanged) return;

                        const previousOrder = lastSavedThemeOrder.slice();
                        const previousThemes = linkedThemes.value.slice();
                        isReorderingThemes.value = true;
                        try {
                                const categoryidNum = Number(categoryId);
                                // Persist the new serialnumber for every theme whose position changed,
                                // in a single batch PUT request.
                                const updates = linkedThemes.value
                                        .map((theme, index) => ({ theme, index }))
                                        .filter(({ theme, index }) => previousOrder[index] !== theme.id)
                                        .map(({ theme, index }) => ({
                                                themeid: theme.id,
                                                categoryid: categoryidNum,
                                                serialnumber: index,
                                        }));
                                if (updates.length > 0) {
                                        await ThemeCategoryLinkService.updateLinks(updates);
                                }
                                lastSavedThemeOrder = newOrder;
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Theme order updated',
                                        showConfirmButton: false,
                                        timer: 2000,
                                });
                        } catch (error: any) {
                                console.error('Failed to reorder linked themes:', error);
                                // Revert local order on failure to keep UI consistent with server state.
                                const byId = new Map(previousThemes.map(t => [t.id, t]));
                                linkedThemes.value = previousOrder
                                        .map(id => byId.get(id))
                                        .filter((t): t is ThemeModel => t != null);
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'error',
                                        title: error.message || 'Failed to update theme order',
                                        showConfirmButton: false,
                                        timer: 4000,
                                });
                        } finally {
                                isReorderingThemes.value = false;
                        }
                };

                const loadAvailableThemes = async () => {
                        const groupid = category.value.groupid;
                        const typeid = category.value.typeid;
                        if (groupid == null || typeid == null) {
                                availableThemes.value = [];
                                return;
                        }
                        isLoadingAvailableThemes.value = true;
                        try {
                                const params = new URLSearchParams({
                                        limit: '0',
                                        where: JSON.stringify({ groupid, typeid }),
                                });
                                const themes = await ThemeService.getAll(params) as ThemeModel[];
                                availableThemes.value = themes;
                        } catch (error: any) {
                                console.error('Failed to load available themes:', error);
                                availableThemes.value = [];
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'error',
                                        title: error.message || 'Failed to load themes for this category',
                                        showConfirmButton: false,
                                        timer: 4000,
                                });
                        } finally {
                                isLoadingAvailableThemes.value = false;
                        }
                };

                const linkTheme = async (theme: ThemeModel) => {
                        if (linkedThemeIds.value.has(theme.id) || isLinking.value) return;
                        isLinking.value = true;
                        try {
                                await ThemeCategoryLinkService.addLink({
                                        themeid: theme.id,
                                        categoryid: Number(categoryId),
                                        serialnumber: linkedThemes.value.length,
                                });
                                // Refresh from server to keep ordering & data consistent
                                await loadLinkedThemes();
                                themeSearch.value = '';
                                themePickerOpen.value = false;
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: `Linked "${theme.name || theme.id}"`,
                                        showConfirmButton: false,
                                        timer: 2500,
                                });
                        } catch (error: any) {
                                console.error('Failed to link theme:', error);
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'error',
                                        title: error.message || 'Failed to link theme',
                                        showConfirmButton: false,
                                        timer: 4000,
                                });
                        } finally {
                                isLinking.value = false;
                        }
                };

                const unlinkTheme = async (theme: ThemeModel) => {
                        if (unlinkingThemeId.value !== null) return;
                        const result = await Swal.fire({
                                title: 'Unlink theme?',
                                text: `Remove "${theme.name || theme.id}" from this category?`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'Unlink',
                                cancelButtonText: 'Cancel',
                                confirmButtonColor: '#dc2626',
                        });
                        if (!result.isConfirmed) return;
                        unlinkingThemeId.value = theme.id;
                        try {
                                await ThemeCategoryLinkService.removeLink(theme.id, Number(categoryId));
                                linkedThemes.value = linkedThemes.value.filter(t => t.id !== theme.id);
                                lastSavedThemeOrder = linkedThemes.value.map(t => t.id);
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: `Unlinked "${theme.name || theme.id}"`,
                                        showConfirmButton: false,
                                        timer: 2500,
                                });
                        } catch (error: any) {
                                console.error('Failed to unlink theme:', error);
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'error',
                                        title: error.message || 'Failed to unlink theme',
                                        showConfirmButton: false,
                                        timer: 4000,
                                });
                        } finally {
                                unlinkingThemeId.value = null;
                        }
                };

                const handleThemePickerClickOutside = (event: MouseEvent) => {
                        if (!themePickerOpen.value) return;
                        const el = themePickerRef.value;
                        if (el && !el.contains(event.target as Node)) {
                                themePickerOpen.value = false;
                        }
                };

                const loadTrackers = async () => {
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const data = await AffiliateService.getAll(params);
                                trackers.value = data;
                        } catch (error: any) {
                                console.error('Failed to load trackers:', error);
                        }
                };

                const saveCategory = async () => {
                        if (isSaving.value) return;
                        isSaving.value = true;
                        try {
                                await ThemeCategoryService.update(categoryId, category.value);
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Category saved successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to save category',
                                        icon: 'error',
                                });
                        } finally {
                                isSaving.value = false;
                        }
                };

                const uploadIcon = () => {
                        if (!filestackClient) {
                                console.error('Filestack client not available');
                                Swal.fire({
                                        title: 'Error',
                                        text: 'File upload service is not available. Please try again later.',
                                        icon: 'error',
                                });
                                return;
                        }

                        const options = {
                                fromSources: ['local_file_system'],
                                accept: ['image/*'],
                                onUploadDone: (response: PickerResponse) => {
                                        if (response.filesUploaded.length > 0) {
                                                category.value.icon = response.filesUploaded[0].url;
                                                iconFileName.value = response.filesUploaded[0].filename;
                                        }
                                },
                        };
                        filestackClient.picker(options).open();
                };

                const removeIcon = () => {
                        category.value.icon = '';
                        iconFileName.value = '';
                };

                const viewTheme = (themeId: number) => {
                        router.push(`/theme/${themeId}`);
                };

                onMounted(async () => {
                        document.addEventListener('mousedown', handleThemePickerClickOutside);
                        await Promise.all([loadCategory(), loadLinkedThemes(), loadTrackers()]);
                        // Available themes depend on category groupid/typeid being loaded
                        await loadAvailableThemes();
                });

                onBeforeUnmount(() => {
                        document.removeEventListener('mousedown', handleThemePickerClickOutside);
                });

                // If groupid/typeid change after load, refetch the catalog
                watch(
                        () => [category.value.groupid, category.value.typeid] as const,
                        (next, prev) => {
                                if (!prev || next[0] !== prev[0] || next[1] !== prev[1]) {
                                        loadAvailableThemes();
                                }
                        },
                );

                // Debounce the active search term used for filtering, so rapid typing
                // doesn't thrash the computed result list.
                const debouncedThemeSearch = ref('');
                let debounceTimer: ReturnType<typeof setTimeout> | null = null;
                watch(themeSearch, (val) => {
                        if (debounceTimer) clearTimeout(debounceTimer);
                        debounceTimer = setTimeout(() => {
                                debouncedThemeSearch.value = val;
                        }, 200);
                });
                onBeforeUnmount(() => {
                        if (debounceTimer) clearTimeout(debounceTimer);
                });

                return {
                        categoryId,
                        category,
                        linkedThemes,
                        trackers,
                        isLoading,
                        isSaving,
                        isLoadingThemes,
                        activeTab,
                        iconFileName,
                        tabs,
                        availableThemes,
                        isLoadingAvailableThemes,
                        themeSearch,
                        themePickerOpen,
                        themePickerRef,
                        isLinking,
                        unlinkingThemeId,
                        isReorderingThemes,
                        onReorderLinkedThemes,
                        linkedThemeIds,
                        canSearchThemes,
                        filteredAvailableThemes,
                        saveCategory,
                        uploadIcon,
                        removeIcon,
                        viewTheme,
                        linkTheme,
                        unlinkTheme,
                };
        },
});
</script>
